import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

import { purchaseApi } from "./api";
import { dateText, errorMessage, money } from "./utils";
import "./QuotationPage.css";

const emptyItem = () => ({
  description: "",
  specification: "",
  qty: 1,
  unit: "NOS",
  rate: 0,
});

const initialForm = () => ({
  quotationDate: new Date().toISOString().slice(0, 10),

  vendor: "",
  projectId: "",
  projectName: "",

  company: "SDC",
  status: "Draft",

  items: [emptyItem()],

  discountPercent: 0,
  gstPercent: 18,
  gstType: "CGST_SGST",

  termsAndConditions: "",
  remark: "",
});

const round2 = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const getAttachmentName = (file, index = 0) =>
  file?.originalName || file?.fileName || file?.name || `Document ${index + 1}`;

const getAttachmentUrl = (file) =>
  file?.url || file?.fileUrl || file?.downloadUrl || "";

export default function QuotationPage() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);

  const [form, setForm] = useState(initialForm());
  const [editing, setEditing] = useState(null);

  const [attachments, setAttachments] = useState([]);

  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const [search, setSearch] = useState("");
  const [year, setYear] = useState("");
  const [quarter, setQuarter] = useState("");
  const [month, setMonth] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* =========================================================
     LOAD DATA
  ========================================================= */

  const load = async () => {
    try {
      setLoading(true);
      setError("");

      const [quotationResponse, vendorResponse, projectResponse] =
        await Promise.all([
          purchaseApi.quotations.list(),
          purchaseApi.vendors.list(),
          purchaseApi.projects.list(),
        ]);

      const quotationData = quotationResponse?.data || {};
      const vendorData = vendorResponse?.data || {};
      const projectData = projectResponse?.data;

      setRows(
        Array.isArray(quotationData.quotations)
          ? quotationData.quotations
          : Array.isArray(quotationData)
            ? quotationData
            : [],
      );

      setVendors(
        Array.isArray(vendorData.vendors)
          ? vendorData.vendors
          : Array.isArray(vendorData)
            ? vendorData
            : [],
      );

      if (Array.isArray(projectData)) {
        setProjects(projectData);
      } else if (Array.isArray(projectData?.projects)) {
        setProjects(projectData.projects);
      } else if (Array.isArray(projectData?.data)) {
        setProjects(projectData.data);
      } else {
        setProjects([]);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* =========================================================
     SELECT OPTIONS
  ========================================================= */

  const vendorOptions = useMemo(() => {
    return vendors
      .map((vendor) => ({
        value: vendor._id,
        label:
          vendor.vendorName ||
          vendor.name ||
          vendor.companyName ||
          "Unnamed Vendor",
        data: vendor,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [vendors]);

  const projectOptions = useMemo(() => {
    return projects
      .filter((project) => project?._id && project?.name)
      .map((project) => ({
        value: project._id,
        label: project.name,
        data: project,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projects]);

  /* =========================================================
     DYNAMIC FILTERS
  ========================================================= */

  const filterMeta = useMemo(() => {
    const map = {};

    rows.forEach((row) => {
      if (!row.quotationDate) return;

      const d = new Date(row.quotationDate);

      if (Number.isNaN(d.getTime())) return;

      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const q = Math.floor(d.getMonth() / 3) + 1;

      if (!map[y]) {
        map[y] = {
          months: new Set(),
          quarters: new Set(),
        };
      }

      map[y].months.add(m);
      map[y].quarters.add(q);
    });

    return Object.keys(map)
      .map(Number)
      .sort((a, b) => b - a)
      .map((y) => ({
        year: y,
        months: Array.from(map[y].months).sort((a, b) => a - b),
        quarters: Array.from(map[y].quarters).sort((a, b) => a - b),
      }));
  }, [rows]);

  const selectedYearFilter = useMemo(() => {
    return filterMeta.find((item) => String(item.year) === String(year));
  }, [filterMeta, year]);

  /* =========================================================
     FILTERED ROWS
  ========================================================= */

  const filteredRows = useMemo(() => {
    const searchValue = String(search || "")
      .trim()
      .toLowerCase();

    return rows.filter((row) => {
      if (searchValue) {
        const searchable = [
          row.quotationNo,
          row.vendorName,
          row.projectName,
          row.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(searchValue)) {
          return false;
        }
      }

      if (year || month || quarter) {
        if (!row.quotationDate) return false;

        const d = new Date(row.quotationDate);

        if (Number.isNaN(d.getTime())) return false;

        const rowYear = d.getFullYear();
        const rowMonth = d.getMonth() + 1;
        const rowQuarter = Math.floor(d.getMonth() / 3) + 1;

        if (year && String(rowYear) !== String(year)) {
          return false;
        }

        if (month && String(rowMonth) !== String(month)) {
          return false;
        }

        if (quarter && String(rowQuarter) !== String(quarter)) {
          return false;
        }
      }

      return true;
    });
  }, [rows, search, year, month, quarter]);

  const totalAmount = useMemo(() => {
    return filteredRows.reduce(
      (sum, row) => sum + Number(row.grandTotal || 0),
      0,
    );
  }, [filteredRows]);

  /* =========================================================
     FORM CALCULATION
  ========================================================= */

  const calculation = useMemo(() => {
    const items = Array.isArray(form.items) ? form.items : [];

    const subtotal = round2(
      items.reduce(
        (sum, item) => sum + Number(item.qty || 0) * Number(item.rate || 0),
        0,
      ),
    );

    const discountPercent = Number(form.discountPercent || 0);

    const discountAmount = round2(subtotal * (discountPercent / 100));

    const taxableAmount = round2(Math.max(subtotal - discountAmount, 0));

    const gstPercent = Number(form.gstPercent || 0);

    const gstAmount = round2(taxableAmount * (gstPercent / 100));

    const grandTotal = round2(taxableAmount + gstAmount);

    return {
      subtotal,
      discountAmount,
      taxableAmount,
      gstAmount,
      grandTotal,

      cgstAmount: form.gstType === "CGST_SGST" ? round2(gstAmount / 2) : 0,

      sgstAmount: form.gstType === "CGST_SGST" ? round2(gstAmount / 2) : 0,

      igstAmount: form.gstType === "IGST" ? gstAmount : 0,
    };
  }, [form.items, form.discountPercent, form.gstPercent, form.gstType]);

  /* =========================================================
     CREATE
  ========================================================= */

  const openCreate = () => {
    setError("");
    setEditing(null);
    setAttachments([]);
    setForm(initialForm());
    setOpen(true);
  };

  /* =========================================================
     EDIT
  ========================================================= */

  const edit = async (id) => {
    try {
      setError("");

      const { data } = await purchaseApi.quotations.get(id);

      const quotation = data?.quotation || data;

      if (!quotation?._id) {
        throw new Error("Quotation not found");
      }

      setEditing(quotation);
      setAttachments([]);

      setForm({
        quotationDate: quotation.quotationDate
          ? new Date(quotation.quotationDate).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10),

        vendor: quotation.vendor?._id || quotation.vendor || "",

        projectId: quotation.projectId?._id || quotation.projectId || "",

        projectName: quotation.projectName || "",

        company: "SDC",

        status: quotation.status || "Draft",

        items:
          Array.isArray(quotation.items) && quotation.items.length
            ? quotation.items.map((item) => ({
                ...item,

                description: item.description || "",

                specification: item.specification || "",

                qty: Number(item.qty || 0),

                unit: item.unit || "NOS",

                rate: Number(item.rate || 0),
              }))
            : [emptyItem()],

        discountPercent: Number(quotation.discountPercent || 0),

        gstPercent: Number(quotation.gstPercent ?? 18),

        gstType: quotation.gstType || "CGST_SGST",

        termsAndConditions: quotation.termsAndConditions || "",

        remark: quotation.remark || "",
      });

      setOpen(true);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  /* =========================================================
     CLOSE
  ========================================================= */

  const closeForm = () => {
    if (saving) return;

    setOpen(false);
    setEditing(null);
    setAttachments([]);
    setForm(initialForm());
  };

  /* =========================================================
     CHANGE
  ========================================================= */

  const changeForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  /* =========================================================
     VENDOR
  ========================================================= */

  const handleVendorChange = (option) => {
    changeForm("vendor", option?.value || "");
  };

  /* =========================================================
     PROJECT
  ========================================================= */

  const handleProjectChange = (option) => {
    setForm((previous) => ({
      ...previous,

      projectId: option?.value || "",

      projectName: option?.data?.name || option?.label || "",
    }));
  };

  /* =========================================================
     ITEMS
  ========================================================= */

  const updateItem = (index, field, value) => {
    setForm((previous) => {
      const items = [...previous.items];

      items[index] = {
        ...items[index],
        [field]: value,
      };

      return {
        ...previous,
        items,
      };
    });
  };

  const addItem = () => {
    setForm((previous) => ({
      ...previous,
      items: [...previous.items, emptyItem()],
    }));
  };

  const removeItem = (index) => {
    setForm((previous) => {
      if (previous.items.length <= 1) {
        return previous;
      }

      return {
        ...previous,
        items: previous.items.filter((_, i) => i !== index),
      };
    });
  };

  /* =========================================================
     ATTACHMENTS
  ========================================================= */

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    setAttachments((previous) => [...previous, ...files]);

    e.target.value = "";
  };

  const removeNewAttachment = (index) => {
    setAttachments((previous) =>
      previous.filter((_, fileIndex) => fileIndex !== index),
    );
  };

  /* =========================================================
     SAVE
  ========================================================= */

  const save = async (e) => {
    e.preventDefault();

    try {
      setError("");

      if (!form.vendor) {
        setError("Please select vendor.");
        return;
      }

      const validItems = form.items
        .map((item) => ({
          ...item,

          description: String(item.description || "").trim(),

          specification: String(item.specification || "").trim(),

          qty: Number(item.qty || 0),

          rate: Number(item.rate || 0),

          unit: String(item.unit || "NOS").trim() || "NOS",
        }))
        .filter((item) => item.description);

      if (!validItems.length) {
        setError("Please add at least one quotation item.");
        return;
      }

      setSaving(true);

      const data = new FormData();

      data.append("quotationDate", form.quotationDate);

      data.append("vendor", form.vendor);

      if (form.projectId) {
        data.append("projectId", form.projectId);
      }

      data.append("projectName", form.projectName || "");

      data.append("company", "SDC");

      data.append("status", form.status || "Draft");

      data.append("items", JSON.stringify(validItems));

      data.append("discountPercent", String(Number(form.discountPercent || 0)));

      data.append("gstPercent", String(Number(form.gstPercent || 0)));

      data.append("gstType", form.gstType || "CGST_SGST");

      data.append("termsAndConditions", form.termsAndConditions || "");

      data.append("remark", form.remark || "");

      attachments.forEach((file) => {
        data.append("attachments", file);
      });

      if (editing?._id) {
        await purchaseApi.quotations.update(editing._id, data);
      } else {
        await purchaseApi.quotations.create(data);
      }

      setOpen(false);
      setEditing(null);
      setAttachments([]);
      setForm(initialForm());

      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const remove = async (id) => {
    if (!window.confirm("Delete this quotation?")) {
      return;
    }

    try {
      setError("");

      await purchaseApi.quotations.remove(id);

      await load();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  /* =========================================================
     PREVIEW
  ========================================================= */
  const openAttachment = async (file) => {
    try {
      setError("");

      if (!file?.fileName) {
        setError("Document file not found.");
        return;
      }

      const response = await purchaseApi.quotations.openFile(file.fileName);

      const contentType =
        response.headers?.["content-type"] ||
        file.mimeType ||
        "application/octet-stream";

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const blobUrl = window.URL.createObjectURL(blob);

      window.open(blobUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (e) {
      console.error("Open attachment error:", e);

      setError(errorMessage(e) || "Unable to open attached document.");
    }
  };
  const openPreview = async (row) => {
    try {
      setError("");

      const { data } = await purchaseApi.quotations.get(row._id);

      setPreview(data?.quotation || data || row);
    } catch (e) {
      setPreview(row);
    }
  };

  const printQuotation = () => {
    window.print();
  };

  /* =========================================================
     FILTER HELPERS
  ========================================================= */

  const getMonthName = (monthNo) => {
    return new Date(2000, Number(monthNo) - 1, 1).toLocaleString("en-IN", {
      month: "long",
    });
  };

  const clearFilters = () => {
    setSearch("");
    setYear("");
    setQuarter("");
    setMonth("");
  };

  /* =========================================================
     SELECTED VALUES
  ========================================================= */

  const selectedVendor =
    vendorOptions.find(
      (option) => String(option.value) === String(form.vendor),
    ) || null;

  const selectedProject =
    projectOptions.find(
      (option) => String(option.value) === String(form.projectId),
    ) || null;

  /* =========================================================
     PREVIEW CALCULATION
  ========================================================= */

  const previewCalculation = useMemo(() => {
    if (!preview) return null;

    const items = Array.isArray(preview.items) ? preview.items : [];

    const subtotal =
      preview.subtotal != null
        ? Number(preview.subtotal)
        : round2(
            items.reduce(
              (sum, item) =>
                sum + Number(item.qty || 0) * Number(item.rate || 0),
              0,
            ),
          );

    const discountPercent = Number(preview.discountPercent || 0);

    const discountAmount =
      preview.discountAmount != null
        ? Number(preview.discountAmount)
        : round2(subtotal * (discountPercent / 100));

    const taxableAmount =
      preview.taxableAmount != null
        ? Number(preview.taxableAmount)
        : round2(Math.max(subtotal - discountAmount, 0));

    const gstPercent = Number(preview.gstPercent || 0);

    const gstAmount =
      preview.gstAmount != null
        ? Number(preview.gstAmount)
        : round2(taxableAmount * (gstPercent / 100));

    const grandTotal =
      preview.grandTotal != null
        ? Number(preview.grandTotal)
        : round2(taxableAmount + gstAmount);

    return {
      subtotal,
      discountPercent,
      discountAmount,
      taxableAmount,
      gstPercent,
      gstAmount,
      grandTotal,

      cgstAmount:
        preview.cgstAmount != null
          ? Number(preview.cgstAmount)
          : preview.gstType === "CGST_SGST"
            ? round2(gstAmount / 2)
            : 0,

      sgstAmount:
        preview.sgstAmount != null
          ? Number(preview.sgstAmount)
          : preview.gstType === "CGST_SGST"
            ? round2(gstAmount / 2)
            : 0,

      igstAmount:
        preview.igstAmount != null
          ? Number(preview.igstAmount)
          : preview.gstType === "IGST"
            ? gstAmount
            : 0,
    };
  }, [preview]);

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="quotation-page">
      {/* ================= HEADER ================= */}

      <div className="quotation-head">
        <div>
          <h1>Purchase Quotation</h1>

          <p>Create and manage vendor quotations.</p>
        </div>

        <button
          type="button"
          className="quotation-btn primary"
          onClick={openCreate}
        >
          + Create Quotation
        </button>
      </div>

      {error && <div className="quotation-error">{error}</div>}

      {/* ================= FILTERS ================= */}

      <div className="quotation-card quotation-filter-card">
        <div className="quotation-filter-row">
          <div className="quotation-field quotation-search">
            <label>Search</label>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Quotation No. / Vendor / Project"
            />
          </div>

          <div className="quotation-field">
            <label>Year</label>

            <select
              value={year}
              onChange={(e) => {
                setYear(e.target.value);

                setMonth("");
                setQuarter("");
              }}
            >
              <option value="">All Years</option>

              {filterMeta.map((item) => (
                <option key={item.year} value={item.year}>
                  {item.year}
                </option>
              ))}
            </select>
          </div>

          <div className="quotation-field">
            <label>Quarter</label>

            <select
              value={quarter}
              disabled={!year}
              onChange={(e) => {
                setQuarter(e.target.value);

                if (e.target.value) {
                  setMonth("");
                }
              }}
            >
              <option value="">All Quarters</option>

              {selectedYearFilter?.quarters?.map((q) => (
                <option key={q} value={q}>
                  Q{q}
                </option>
              ))}
            </select>
          </div>

          <div className="quotation-field">
            <label>Month</label>

            <select
              value={month}
              disabled={!year}
              onChange={(e) => {
                setMonth(e.target.value);

                if (e.target.value) {
                  setQuarter("");
                }
              }}
            >
              <option value="">All Months</option>

              {selectedYearFilter?.months?.map((m) => (
                <option key={m} value={m}>
                  {getMonthName(m)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="quotation-btn quotation-clear-btn"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>

        <div className="quotation-summary">
          <div className="quotation-stat">
            <small>Records</small>

            <strong>{filteredRows.length}</strong>
          </div>

          <div className="quotation-stat">
            <small>Total Amount</small>

            <strong>{money(totalAmount)}</strong>
          </div>
        </div>
      </div>

      {/* ================= TABLE ================= */}

      <div className="quotation-card quotation-table-card">
        <div className="quotation-table-wrap">
          <table className="quotation-table">
            <thead>
              <tr>
                <th>QUOTATION NO.</th>

                <th>VENDOR</th>

                <th>DATE</th>

                <th>PROJECT</th>

                <th>TOTAL</th>

                <th>STATUS</th>

                <th>ACTION</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="quotation-empty">
                    Loading quotations...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="quotation-empty">
                    No quotations found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <b>{row.quotationNo || "-"}</b>
                    </td>

                    <td>{row.vendorName || "-"}</td>

                    <td>{dateText(row.quotationDate)}</td>

                    <td>{row.projectName || "-"}</td>

                    <td className="quotation-amount">
                      {money(row.grandTotal || 0)}
                    </td>

                    <td>
                      <span className="quotation-status">
                        {row.status || "Draft"}
                      </span>
                    </td>

                    <td>
                      <div className="quotation-actions">
                        <button type="button" onClick={() => edit(row._id)}>
                          Edit
                        </button>

                        <button type="button" onClick={() => openPreview(row)}>
                          Preview / PDF
                        </button>

                        <button
                          type="button"
                          className="danger"
                          onClick={() => remove(row._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =====================================================
          CREATE / EDIT MODAL
      ===================================================== */}

      {open && (
        <div className="quotation-modal-backdrop quotation-no-print">
          <div className="quotation-modal">
            <form onSubmit={save}>
              <div className="quotation-modal-head">
                <div>
                  <h2>{editing ? "Edit Quotation" : "Create Quotation"}</h2>

                  <p>
                    Enter vendor quotation details. Project selection is
                    optional.
                  </p>
                </div>

                <button
                  type="button"
                  className="quotation-modal-close"
                  onClick={closeForm}
                >
                  ×
                </button>
              </div>

              <div className="quotation-modal-body">
                {/* BASIC */}

                <div className="quotation-section">
                  <div className="quotation-section-title">
                    Basic Information
                  </div>

                  <div className="quotation-form-grid">
                    <div className="quotation-form-field">
                      <label>
                        Vendor <span>*</span>
                      </label>

                      <Select
                        options={vendorOptions}
                        value={selectedVendor}
                        onChange={handleVendorChange}
                        placeholder="Search / Select Vendor"
                        isClearable
                        isSearchable
                        classNamePrefix="quotation-select"
                      />
                    </div>

                    <div className="quotation-form-field">
                      <label>
                        Project <small>(Optional)</small>
                      </label>

                      <Select
                        options={projectOptions}
                        value={selectedProject}
                        onChange={handleProjectChange}
                        placeholder="Search / Select Project"
                        isClearable
                        isSearchable
                        noOptionsMessage={() => "No project found"}
                        classNamePrefix="quotation-select"
                      />
                    </div>

                    <div className="quotation-form-field">
                      <label>Quotation Date</label>

                      <input
                        type="date"
                        value={form.quotationDate}
                        onChange={(e) =>
                          changeForm("quotationDate", e.target.value)
                        }
                      />
                    </div>

                    <div className="quotation-form-field">
                      <label>Company</label>

                      <input
                        value="SDC"
                        readOnly
                        className="quotation-readonly"
                      />
                    </div>

                    <div className="quotation-form-field">
                      <label>Status</label>

                      <select
                        value={form.status}
                        onChange={(e) => changeForm("status", e.target.value)}
                      >
                        <option value="Draft">Draft</option>

                        <option value="Pending">Pending</option>

                        <option value="Approved">Approved</option>

                        <option value="Rejected">Rejected</option>

                        <option value="Converted">Converted</option>

                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ITEMS */}

                <div className="quotation-section">
                  <div className="quotation-section-head">
                    <div className="quotation-section-title">
                      Quotation Items
                    </div>

                    <button
                      type="button"
                      className="quotation-btn"
                      onClick={addItem}
                    >
                      + Add Item
                    </button>
                  </div>

                  <div className="quotation-items-wrap">
                    <table className="quotation-items-table">
                      <thead>
                        <tr>
                          <th>#</th>

                          <th>Description</th>

                          <th>Specification</th>

                          <th>Qty</th>

                          <th>Unit</th>

                          <th>Rate</th>

                          <th>Amount</th>

                          <th></th>
                        </tr>
                      </thead>

                      <tbody>
                        {form.items.map((item, index) => {
                          const amount =
                            Number(item.qty || 0) * Number(item.rate || 0);

                          return (
                            <tr key={index}>
                              <td>{index + 1}</td>

                              <td>
                                <input
                                  value={item.description}
                                  placeholder="Item / Work description"
                                  onChange={(e) =>
                                    updateItem(
                                      index,
                                      "description",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  value={item.specification}
                                  placeholder="Specification"
                                  onChange={(e) =>
                                    updateItem(
                                      index,
                                      "specification",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.qty}
                                  onChange={(e) =>
                                    updateItem(index, "qty", e.target.value)
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  value={item.unit}
                                  onChange={(e) =>
                                    updateItem(index, "unit", e.target.value)
                                  }
                                />
                              </td>

                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.rate}
                                  onChange={(e) =>
                                    updateItem(index, "rate", e.target.value)
                                  }
                                />
                              </td>

                              <td className="quotation-item-amount">
                                {money(amount)}
                              </td>

                              <td>
                                <button
                                  type="button"
                                  className="quotation-item-delete"
                                  disabled={form.items.length === 1}
                                  onClick={() => removeItem(index)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ADDITIONAL + TOTAL */}

                <div className="quotation-bottom-grid">
                  <div>
                    <div className="quotation-section">
                      <div className="quotation-section-title">
                        Additional Details
                      </div>

                      <div className="quotation-form-grid two">
                        <div className="quotation-form-field">
                          <label>Discount %</label>

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.discountPercent}
                            onChange={(e) =>
                              changeForm("discountPercent", e.target.value)
                            }
                          />
                        </div>

                        <div className="quotation-form-field">
                          <label>GST %</label>

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.gstPercent}
                            onChange={(e) =>
                              changeForm("gstPercent", e.target.value)
                            }
                          />
                        </div>

                        <div className="quotation-form-field">
                          <label>GST Type</label>

                          <select
                            value={form.gstType}
                            onChange={(e) =>
                              changeForm("gstType", e.target.value)
                            }
                          >
                            <option value="CGST_SGST">CGST + SGST</option>

                            <option value="IGST">IGST</option>
                          </select>
                        </div>
                      </div>

                      <div className="quotation-form-field quotation-textarea-field">
                        <label>Terms & Conditions</label>

                        <textarea
                          rows="4"
                          value={form.termsAndConditions}
                          onChange={(e) =>
                            changeForm("termsAndConditions", e.target.value)
                          }
                          placeholder="Enter quotation terms and conditions..."
                        />
                      </div>

                      <div className="quotation-form-field quotation-textarea-field">
                        <label>Remark</label>

                        <textarea
                          rows="3"
                          value={form.remark}
                          onChange={(e) => changeForm("remark", e.target.value)}
                          placeholder="Internal remark..."
                        />
                      </div>

                      {/* ================= ATTACHMENTS ================= */}

                      <div className="quotation-attachment-section">
                        <div className="quotation-section-title">
                          Supporting Documents
                        </div>

                        <div className="quotation-upload-box">
                          <input
                            id="quotation-attachments"
                            type="file"
                            multiple
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                            onChange={handleAttachmentChange}
                          />

                          <label
                            htmlFor="quotation-attachments"
                            className="quotation-attach-btn"
                          >
                            + Attach Documents
                          </label>

                          <span className="quotation-upload-help">
                            PDF, JPG, PNG, Word or Excel
                          </span>
                        </div>

                        {editing?.attachments?.length > 0 && (
                          <div className="quotation-file-list">
                            <div className="quotation-file-heading">
                              Existing Documents
                            </div>

                            {editing.attachments.map((file, index) => (
                              <div
                                className="quotation-file-row"
                                key={file._id || index}
                              >
                                <button
                                  type="button"
                                  className="quotation-file-open"
                                  onClick={() => openAttachment(file)}
                                  title="Open document"
                                >
                                  <span className="quotation-file-icon">
                                    📎
                                  </span>

                                  <span className="quotation-file-name">
                                    {getAttachmentName(file, index)}
                                  </span>

                                  <span className="quotation-file-open-text">
                                    Open
                                  </span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {attachments.length > 0 && (
                          <div className="quotation-file-list">
                            <div className="quotation-file-heading">
                              New Documents
                            </div>

                            {attachments.map((file, index) => (
                              <div
                                className="quotation-file-row"
                                key={`${file.name}-${index}`}
                              >
                                <div className="quotation-file-info">
                                  <span className="quotation-file-icon">
                                    📎
                                  </span>

                                  <div>
                                    <div>{file.name}</div>

                                    <small>
                                      {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </small>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="quotation-file-remove"
                                  onClick={() => removeNewAttachment(index)}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* TOTAL */}

                  <div className="quotation-total-box">
                    <h3>Quotation Summary</h3>

                    <div className="quotation-total-row">
                      <span>Subtotal</span>

                      <strong>{money(calculation.subtotal)}</strong>
                    </div>

                    <div className="quotation-total-row">
                      <span>
                        Discount ({Number(form.discountPercent || 0)}
                        %)
                      </span>

                      <strong>- {money(calculation.discountAmount)}</strong>
                    </div>

                    <div className="quotation-total-row">
                      <span>Taxable Amount</span>

                      <strong>{money(calculation.taxableAmount)}</strong>
                    </div>

                    {form.gstType === "CGST_SGST" ? (
                      <>
                        <div className="quotation-total-row">
                          <span>
                            CGST ({Number(form.gstPercent || 0) / 2}
                            %)
                          </span>

                          <strong>{money(calculation.cgstAmount)}</strong>
                        </div>

                        <div className="quotation-total-row">
                          <span>
                            SGST ({Number(form.gstPercent || 0) / 2}
                            %)
                          </span>

                          <strong>{money(calculation.sgstAmount)}</strong>
                        </div>
                      </>
                    ) : (
                      <div className="quotation-total-row">
                        <span>
                          IGST ({Number(form.gstPercent || 0)}
                          %)
                        </span>

                        <strong>{money(calculation.igstAmount)}</strong>
                      </div>
                    )}

                    <div className="quotation-total-row grand">
                      <span>Grand Total</span>

                      <strong>{money(calculation.grandTotal)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="quotation-modal-footer">
                <button
                  type="button"
                  className="quotation-btn"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="quotation-btn primary"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editing
                      ? "Update Quotation"
                      : "Save Quotation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =====================================================
          PROFESSIONAL A4 PREVIEW
      ===================================================== */}

      {preview && previewCalculation && (
        <div className="quotation-preview-overlay">
          <div className="quotation-preview-shell">
            {/* ACTION BAR */}

            <div className="quotation-preview-actions quotation-no-print">
              <div>
                <strong>Quotation Preview</strong>

                <span>{preview.quotationNo || ""}</span>
              </div>

              <div className="quotation-preview-action-buttons">
                <button type="button" onClick={printQuotation}>
                  Print / Save PDF
                </button>

                <button type="button" onClick={() => setPreview(null)}>
                  Close
                </button>
              </div>
            </div>

            {/* A4 */}

            <div className="quotation-a4" id="quotation-print-area">
              {/* TOP HEADER */}

              <div className="quotation-pdf-header">
                <div className="quotation-pdf-brand">
                  <div className="quotation-pdf-logo">SDC</div>

                  <div>
                    <h1>SDC</h1>

                    <p>Purchase Department</p>
                  </div>
                </div>

                <div className="quotation-pdf-title">
                  <h2>QUOTATION</h2>

                  <div>
                    <span>Quotation No.</span>

                    <strong>{preview.quotationNo || "-"}</strong>
                  </div>

                  <div>
                    <span>Date</span>

                    <strong>{dateText(preview.quotationDate)}</strong>
                  </div>
                </div>
              </div>

              {/* BLUE LINE */}

              <div className="quotation-pdf-accent"></div>

              {/* INFO */}

              <div className="quotation-pdf-info-grid">
                <div className="quotation-pdf-info-box">
                  <div className="quotation-pdf-info-title">VENDOR DETAILS</div>

                  <h3>
                    {preview.vendorName || preview.vendor?.vendorName || "-"}
                  </h3>

                  {preview.contactPerson && (
                    <p>
                      <b>Contact:</b> {preview.contactPerson}
                    </p>
                  )}

                  {preview.contactNo && (
                    <p>
                      <b>Mobile:</b> {preview.contactNo}
                    </p>
                  )}

                  {preview.gstNo && (
                    <p>
                      <b>GSTIN:</b> {preview.gstNo}
                    </p>
                  )}
                </div>

                <div className="quotation-pdf-info-box">
                  <div className="quotation-pdf-info-title">
                    QUOTATION DETAILS
                  </div>

                  <div className="quotation-pdf-meta-row">
                    <span>Project</span>

                    <strong>{preview.projectName || "General"}</strong>
                  </div>

                  <div className="quotation-pdf-meta-row">
                    <span>Company</span>

                    <strong>SDC</strong>
                  </div>

                  <div className="quotation-pdf-meta-row">
                    <span>Status</span>

                    <strong>{preview.status || "Draft"}</strong>
                  </div>
                </div>
              </div>

              {/* ITEMS */}

              <div className="quotation-pdf-section-title">Quotation Items</div>

              <table className="quotation-pdf-table">
                <thead>
                  <tr>
                    <th className="pdf-col-sr">#</th>

                    <th>Description</th>

                    <th>Specification</th>

                    <th className="pdf-col-small">Qty</th>

                    <th className="pdf-col-small">Unit</th>

                    <th className="pdf-col-money">Rate</th>

                    <th className="pdf-col-money">Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {(preview.items || []).map((item, index) => {
                    const amount =
                      item.amount != null
                        ? Number(item.amount)
                        : Number(item.qty || 0) * Number(item.rate || 0);

                    return (
                      <tr key={item._id || index}>
                        <td className="pdf-center">{index + 1}</td>

                        <td>
                          <strong>{item.description || "-"}</strong>
                        </td>

                        <td>{item.specification || "-"}</td>

                        <td className="pdf-center">{item.qty || 0}</td>

                        <td className="pdf-center">{item.unit || "-"}</td>

                        <td className="pdf-right">{money(item.rate || 0)}</td>

                        <td className="pdf-right pdf-bold">{money(amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* TOTALS */}

              <div className="quotation-pdf-total-area">
                <div className="quotation-pdf-total-spacer"></div>

                <div className="quotation-pdf-total-card">
                  <div>
                    <span>Subtotal</span>

                    <strong>{money(previewCalculation.subtotal)}</strong>
                  </div>

                  {previewCalculation.discountPercent > 0 && (
                    <div>
                      <span>
                        Discount ({previewCalculation.discountPercent}
                        %)
                      </span>

                      <strong>
                        - {money(previewCalculation.discountAmount)}
                      </strong>
                    </div>
                  )}

                  <div>
                    <span>Taxable Amount</span>

                    <strong>{money(previewCalculation.taxableAmount)}</strong>
                  </div>

                  {preview.gstType === "IGST" ? (
                    <div>
                      <span>
                        IGST ({previewCalculation.gstPercent}
                        %)
                      </span>

                      <strong>{money(previewCalculation.igstAmount)}</strong>
                    </div>
                  ) : (
                    <>
                      <div>
                        <span>
                          CGST ({previewCalculation.gstPercent / 2}
                          %)
                        </span>

                        <strong>{money(previewCalculation.cgstAmount)}</strong>
                      </div>

                      <div>
                        <span>
                          SGST ({previewCalculation.gstPercent / 2}
                          %)
                        </span>

                        <strong>{money(previewCalculation.sgstAmount)}</strong>
                      </div>
                    </>
                  )}

                  <div className="quotation-pdf-grand-total">
                    <span>GRAND TOTAL</span>

                    <strong>{money(previewCalculation.grandTotal)}</strong>
                  </div>
                </div>
              </div>

              {/* TERMS */}

              {(preview.termsAndConditions || preview.remark) && (
                <div className="quotation-pdf-bottom-content">
                  {preview.termsAndConditions && (
                    <div className="quotation-pdf-notes">
                      <h4>Terms & Conditions</h4>

                      <p>{preview.termsAndConditions}</p>
                    </div>
                  )}

                  {preview.remark && (
                    <div className="quotation-pdf-notes">
                      <h4>Remarks</h4>

                      <p>{preview.remark}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ATTACHMENTS */}

              {preview.attachments?.length > 0 && (
                <div className="quotation-pdf-attachments">
                  <h4>Supporting Documents</h4>

                  <div>
                    {preview.attachments.map((file, index) => {
                      const url = getAttachmentUrl(file);

                      return url ? (
                        <a
                          key={file._id || index}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          📎 {getAttachmentName(file, index)}
                        </a>
                      ) : (
                        <span key={file._id || index}>
                          📎 {getAttachmentName(file, index)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* SIGNATURE */}

              <div className="quotation-pdf-signature">
                <div>
                  <span>Prepared By</span>

                  <strong>SDC</strong>
                </div>

                <div className="quotation-pdf-sign-box">
                  <span>Authorized Signatory</span>

                  <div></div>

                  <strong>SDC</strong>
                </div>
              </div>

              {/* FOOTER */}

              <div className="quotation-pdf-footer">
                <span>SDC • Purchase Quotation</span>

                <span>{preview.quotationNo || ""}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
